import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User } from 'lucide-react';
import logo from '../assets/logo-sdi-branco.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuButtonRef = useRef(null);
  const mobileNavRef = useRef(null);
  const location = useLocation();
  const loginUrl = process.env.REACT_APP_LOGIN_URL;
  const hasSolidHeader = scrolled || location.pathname !== '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
      if (event.key !== 'Tab') return;

      const focusableElements = [
        menuButtonRef.current,
        ...mobileNavRef.current.querySelectorAll('a[href]')
      ].filter(Boolean);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    mobileNavRef.current.querySelector('a[href]')?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Serviços', path: '/servicos' },
    { name: 'Multisserviços', path: '/multiservicos' },
    { name: 'Resultados', path: '/resultados' },
    { name: 'Sobre', path: '/sobre' },
    { name: 'História', path: '/historia' },
    { name: 'Compromisso', path: '/compromisso' },
    { name: 'Contato', path: '/contato' },
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'py-4' : 'py-6'
          }`}
      >
        <div className="container mx-auto px-4">
          <div className={`
            relative flex items-center justify-between px-6 py-3 rounded-full border 
            transition-all duration-300
            ${hasSolidHeader
              ? 'bg-[#080a0d]/95 backdrop-blur-md border-white/10 shadow-xl shadow-black/30'
              : 'bg-transparent border-transparent'
            }
          `}>

            {/* Logo */}
            <div className="flex-shrink-0 z-50">
              <Link to="/" className="flex items-center gap-2 group">
                {/* Logo Image */}
                <div className="relative">
                  <img
                    src={logo}
                    alt="SDI Tecnologia"
                    className="h-10 md:h-12 w-auto object-contain"
                  />
                  <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`relative text-xs xl:text-sm font-medium whitespace-nowrap transition-colors duration-300 hover:text-primary
                    ${location.pathname === link.path ? 'text-white' : 'text-gray-400'}
                  `}
                >
                  {link.name}
                  {location.pathname === link.path && (
                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-primary shadow-[0_0_8px_rgba(220,38,38,0.8)]" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            {loginUrl && <div className="hidden lg:flex items-center gap-4">
              <a
                href={loginUrl}
                className="flex items-center gap-2 px-5 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary hover:bg-primary hover:text-white transition-all duration-300 group"
              >
                <User size={16} />
                <span className="font-medium">Login</span>
              </a>
            </div>}

            {/* Mobile Menu Toggle */}
            <button
              ref={menuButtonRef}
              onClick={toggleMenu}
              type="button"
              aria-label={isMenuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
              className="lg:hidden z-50 p-2 text-white hover:text-primary transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      {isMenuOpen && <div
        ref={mobileNavRef}
        id="mobile-navigation"
        role="dialog"
        aria-modal="true"
        aria-label="Menu principal"
        className="fixed inset-0 z-40 bg-background/95 backdrop-blur-xl lg:hidden flex flex-col items-center justify-center gap-8 overflow-y-auto py-28"
      >
        <nav className="flex flex-col items-center gap-4">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              onClick={() => setIsMenuOpen(false)}
              className="text-2xl font-heading font-bold text-white hover:text-primary transition-colors"
            >
              {link.name}
            </Link>
          ))}
          {loginUrl && <a
            href={loginUrl}
            onClick={() => setIsMenuOpen(false)}
            className="mt-4 px-8 py-3 rounded-full bg-primary text-white font-bold shadow-lg shadow-primary/25"
          >
            Acessar Conta
          </a>}
        </nav>
      </div>}
    </>
  );
};

export default Header;
